import { Controller } from "@hotwired/stimulus"

// Floating Tab Bar with Scroll Behavior & Active Tab State Management
// - Hides/shows tab bar based on scroll direction for immersive mobile experience
// - Updates active tab state on Turbo navigation (data-turbo-permanent compatible)
// Usage: data-controller="floating-tabbar" data-floating-tabbar-scroll-threshold-value="50"
export default class extends Controller {
  static targets = ["tab"]
  static values = {
    scrollThreshold: { type: Number, default: 50 }
  }

  connect() {
    this.lastScrollY = window.scrollY
    this.isScrolledDown = false
    this.ticking = false

    // Bind scroll listener
    this.boundOnScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.boundOnScroll, { passive: true })

    // Bind Turbo navigation listener for active tab updates
    // data-turbo-permanent preserves DOM, so we need to update active state client-side
    this.boundUpdateActiveTab = this.updateActiveTab.bind(this)
    document.addEventListener('turbo:load', this.boundUpdateActiveTab)

    // Reset state before Turbo caches the page (prevents flash on restore)
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener('turbo:before-cache', this.boundBeforeCache)

    // Bind click handler for dynamic turbo_action (first=advance, subsequent=replace)
    this.boundHandleTabClick = this.handleTabClick.bind(this)
    this.tabTargets.forEach(tab => {
      tab.addEventListener('click', this.boundHandleTabClick)
    })

    // Initial state
    this.element.classList.add('scrolled-up')

    // Update active tab on initial load (turbo:load doesn't fire on first page load)
    this.updateActiveTab()
  }

  disconnect() {
    window.removeEventListener('scroll', this.boundOnScroll)
    document.removeEventListener('turbo:load', this.boundUpdateActiveTab)
    document.removeEventListener('turbo:before-cache', this.boundBeforeCache)

    // Cleanup tab click listeners
    this.tabTargets.forEach(tab => {
      tab.removeEventListener('click', this.boundHandleTabClick)
    })
  }

  // Dynamic turbo_action for bottom navigation history management
  // First tab switch: advance (push to history) - allows one back navigation
  // Subsequent switches: replace (no history push) - prevents stack pollution
  // Same URL click (home/products only): prevent navigation, scroll to top (no image blur reset)
  // Chat/profile tabs: always reload for real-time data (messages, notifications)
  handleTabClick(event) {
    const tab = event.currentTarget
    const href = tab.href || tab.getAttribute('href')

    // 채팅/프로필은 항상 새로고침 허용 (실시간 데이터)
    const alwaysReloadPaths = ['/chats', '/profile', '/notifications']
    const targetUrl = new URL(href, window.location.origin)
    const shouldAlwaysReload = alwaysReloadPaths.some(p => targetUrl.pathname.startsWith(p))

    // 같은 URL이고 새로고침 불필요한 탭 (홈/상품) → 스크롤만 리셋
    if (!shouldAlwaysReload && this.isSameUrl(href)) {
      event.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const hasNavigated = sessionStorage.getItem('bottomNavHasNavigated')

    if (hasNavigated) {
      // Already navigated via bottom tabs → replace (history swap)
      tab.dataset.turboAction = 'replace'
    } else {
      // First tab navigation → advance (history push)
      tab.dataset.turboAction = 'advance'
      sessionStorage.setItem('bottomNavHasNavigated', 'true')
    }
  }

  // URL 비교 헬퍼 (pathname + query string)
  isSameUrl(href) {
    const currentUrl = new URL(window.location.href)
    const targetUrl = new URL(href, window.location.origin)
    return currentUrl.pathname === targetUrl.pathname &&
           currentUrl.search === targetUrl.search
  }

  onScroll() {
    // Use requestAnimationFrame for 60fps smooth performance
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateTabbarState()
        this.ticking = false
      })
      this.ticking = true
    }
  }

  updateTabbarState() {
    const currentScrollY = window.scrollY
    const scrollDelta = currentScrollY - this.lastScrollY

    // Scrolling down && past threshold
    if (scrollDelta > 0 && currentScrollY > this.scrollThresholdValue) {
      if (!this.isScrolledDown) {
        this.hideTabbar()
      }
    }

    // Scrolling up
    else if (scrollDelta < 0) {
      if (this.isScrolledDown) {
        this.showTabbar()
      }
    }

    this.lastScrollY = currentScrollY
  }

  hideTabbar() {
    this.element.classList.remove('scrolled-up')
    this.element.classList.add('scrolled-down')
    this.isScrolledDown = true

    // Native mobile scroll behavior - subtle opacity change only
    const tabbarInner = this.element.querySelector('div')
    if (tabbarInner) {
      tabbarInner.style.opacity = '0.85'
      // Enhance backdrop blur on scroll
      tabbarInner.classList.remove('backdrop-blur-xl')
      tabbarInner.classList.add('backdrop-blur-xl')
    }
  }

  showTabbar() {
    this.element.classList.remove('scrolled-down')
    this.element.classList.add('scrolled-up')
    this.isScrolledDown = false

    // Reset opacity to full visibility
    const tabbarInner = this.element.querySelector('div')
    if (tabbarInner) {
      tabbarInner.style.opacity = '1'
      // Keep backdrop blur consistent
      tabbarInner.classList.remove('backdrop-blur-xl')
      tabbarInner.classList.add('backdrop-blur-xl')
    }
  }

  // Reset state on Turbo navigation (before page is cached)
  beforeCache() {
    this.element.classList.remove('scrolled-down')
    this.element.classList.add('scrolled-up')

    const tabbarInner = this.element.querySelector('div')
    if (tabbarInner) {
      tabbarInner.style.removeProperty('opacity')
    }

    this.lastScrollY = 0
    this.isScrolledDown = false
  }

  // Update active tab based on current URL path
  // Called on turbo:load and initial page load
  updateActiveTab() {
    // Reset scroll state after navigation (prevents opacity flash on first scroll)
    this.lastScrollY = window.scrollY
    this.isScrolledDown = false
    this.element.classList.remove('scrolled-down')
    this.element.classList.add('scrolled-up')
    const tabbarInner = this.element.querySelector('div')
    if (tabbarInner) {
      tabbarInner.style.removeProperty('opacity')
    }

    const currentPath = window.location.pathname

    this.tabTargets.forEach(tab => {
      const paths = tab.dataset.tabPaths?.split(',') || []
      const isExact = tab.dataset.tabExact === "true"

      const isActive = paths.some(path => {
        if (isExact) return currentPath === path
        if (path === '/') return currentPath === '/'
        return currentPath.startsWith(path)
      })

      this.setTabActive(tab, isActive)
    })
  }

  // Apply active/inactive styles to a tab element
  // Also switches SVG icon between outline and filled variants
  // (Required because data-turbo-permanent preserves DOM across navigation)
  setTabActive(tab, isActive) {
    // Skip if state unchanged (prevents indicator flicker on every turbo:load)
    const wasActive = tab.getAttribute('aria-current') === 'page'
    if (wasActive === isActive) return

    // Remove existing indicator
    const existingIndicator = tab.querySelector('[data-indicator]')
    if (existingIndicator) existingIndicator.remove()

    // Get SVG elements for icon switching
    const svg = tab.querySelector('svg')
    const path = svg?.querySelector('path')

    if (isActive) {
      tab.classList.remove('text-charcoal-500', 'hover:text-charcoal-700')
      tab.classList.add('text-crust-600')
      tab.setAttribute('aria-current', 'page')

      // Switch to filled icon
      const activePathData = tab.dataset.iconPathActive
      if (activePathData && path) {
        path.setAttribute('d', activePathData)
      }
      if (svg) {
        svg.setAttribute('fill', 'currentColor')
        svg.removeAttribute('stroke')
        // Remove stroke-width for filled icons
        if (path) {
          path.removeAttribute('stroke-width')
        }
      }

      // Add active indicator dot
      const indicator = document.createElement('div')
      indicator.className = 'absolute bottom-0.5 h-0.5 w-6 bg-crust-600 animate-scale-in'
      indicator.dataset.indicator = ''
      tab.appendChild(indicator)
    } else {
      tab.classList.remove('text-crust-600')
      tab.classList.add('text-charcoal-500', 'hover:text-charcoal-700')
      tab.removeAttribute('aria-current')

      // Switch to outline icon
      const outlinePathData = tab.dataset.iconPath
      if (outlinePathData && path) {
        path.setAttribute('d', outlinePathData)
      }
      if (svg) {
        svg.setAttribute('fill', 'none')
        svg.setAttribute('stroke', 'currentColor')
        // Restore stroke-width for outline icons
        if (path) {
          path.setAttribute('stroke-width', '2')
        }
      }
    }
  }
}
